/**
 * Parent-domain session cookie: mirrors root `localStorage` keys for Botblitz SSO.
 * Cookie name must match root `src/utils/everyman-session-sync-cookie.ts`.
 */

import Cookies from 'js-cookie';

import { BOTBLITZ_AUTH_LOG_FULL_LS_KEY, BOTBLITZ_SESSION_COOKIE_NAME } from '../constants/pu-sync-storage';
import { isEmbeddedPuIframe } from './config/config';

export { BOTBLITZ_SESSION_COOKIE_NAME };

export const isBotblitzAuthLogFull = (): boolean => {
    try {
        return (
            typeof window !== 'undefined' &&
            (localStorage.getItem(BOTBLITZ_AUTH_LOG_FULL_LS_KEY) === '1' ||
                sessionStorage.getItem(BOTBLITZ_AUTH_LOG_FULL_LS_KEY) === '1')
        );
    } catch {
        return false;
    }
};

/**
 * PU stores `client.accounts` as a JSON array; dtrader MobX expects `accounts[loginid]`.
 * Arrays make `is_logged_in` false and skip `deriv_api.authorize()` on socket open.
 */
export function normalizeClientAccountsShape(accounts: unknown): Record<string, unknown> {
    if (!accounts || typeof accounts !== 'object') return {};
    if (Array.isArray(accounts)) {
        const obj: Record<string, unknown> = {};
        accounts.forEach(row => {
            const r = row as { loginid?: string };
            if (r && typeof r.loginid === 'string') obj[r.loginid] = row;
        });
        return obj;
    }
    return accounts as Record<string, unknown>;
}

/** Rewrite `localStorage['client.accounts']` when it is still an array (e.g. after cookie hydrate). */
export function normalizeClientAccountsInLocalStorage(): boolean {
    try {
        const raw = localStorage.getItem('client.accounts');
        if (!raw) return false;
        const parsed = JSON.parse(raw) as unknown;
        if (!Array.isArray(parsed)) return false;
        const norm = normalizeClientAccountsShape(parsed);
        localStorage.setItem('client.accounts', JSON.stringify(norm));
        return true;
    } catch {
        return false;
    }
}

/**
 * After hydration, align sessionStorage keys `client.init` reads for active account.
 */
export const syncEverymanActiveLoginSessionKeys = (): void => {
    try {
        const id = (localStorage.getItem('active_loginid') || '').trim();
        if (!id) return;
        if (/^(CR|MF|VRTC)/i.test(id)) {
            sessionStorage.setItem('active_loginid', id);
        }
        if (/^(CRW|MFW|VRW)/i.test(id)) {
            sessionStorage.setItem('active_wallet_loginid', id);
        }
        if (/^DOT/i.test(id)) {
            sessionStorage.setItem('active_loginid', id);
        }
    } catch {
        /* ignore */
    }
};

/**
 * Read `botblitz_session` and write all keys into `localStorage` (same as root).
 * Call before `initStore` when dtrader runs in a PU iframe; parent sets cookies with `Domain=.<registrable>` (last two labels).
 */
export const applyEverymanSessionCookieToLocalStorage = (): boolean => {
    if (typeof window === 'undefined' || !isEmbeddedPuIframe()) return false;
    try {
        const raw = Cookies.get(BOTBLITZ_SESSION_COOKIE_NAME);
        if (!raw) return false;
        const o = JSON.parse(raw) as { v?: number; flow?: string; keys?: Record<string, string> };
        if (!o || o.v !== 1 || !o.keys || typeof o.keys !== 'object') return false;

        let count = 0;
        for (const [k, v] of Object.entries(o.keys)) {
            if (typeof k !== 'string' || k === '' || typeof v !== 'string') continue;
            try {
                localStorage.setItem(k, v);
                count += 1;
            } catch {
                /* quota / private mode */
            }
        }

        normalizeClientAccountsInLocalStorage();

        syncEverymanActiveLoginSessionKeys();

        if (isBotblitzAuthLogFull()) {
            // eslint-disable-next-line no-console
            console.info('🖼️ [botblitz-session] hydrated_from_cookie', { flow: o.flow, keys: o.keys });
        } else {
            // eslint-disable-next-line no-console
            console.info('🖼️ [botblitz-session] hydrated_from_cookie', {
                flow: o.flow,
                key_count: count,
                raw_len: raw.length,
            });
        }
        return count > 0;
    } catch (e) {
        console.error('🖼️ [botblitz-session] applyEverymanSessionCookieToLocalStorage failed:', e);
        return false;
    }
};
